import React, { useEffect, useMemo, useState } from "react";

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";

import {
  Button,
  Datagrid,
  ListContextProvider,
  ListToolbar,
  TextField,
  TopToolbar,
  useList,
  useNotify,
} from "react-admin";

import {
  CancelIcon,
  CloseIcon,
  ExportIcon,
} from "../../../../components/common/iconography";
import { Pagination } from "../../../../components/Pagination";
import { SearchField } from "../../../../components/common/SearchField";
import { fetchActiveAccounts } from "../../accounts/AccountUtils";
import { ServicesPeak } from "../../services/ServicesPeak";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    height: "90vh",
  },
  dialogTitle: {
    display: "flex",
    paddingTop: "5px",
    paddingBottom: "5px",
    justifyContent: "space-between",
  },
}));

const AccountSearchField = (props) => {
  const { allAccounts, setAccounts, label, alwaysOn } = props;

  const handleSearch = (value) => {
    const searchTerm = value?.toLowerCase() || "";
    const filtered = allAccounts.filter(
      (acc) =>
        acc.displayName?.toLowerCase().includes(searchTerm) ||
        acc.businessName?.toLowerCase().includes(searchTerm) ||
        acc.lotNumberPrefix?.toLowerCase().includes(searchTerm) ||
        acc.addressState?.toLowerCase().includes(searchTerm),
    );
    setAccounts(filtered);
  };

  return (
    <SearchField
      id="accountSearchTerm"
      label={label}
      margin="dense"
      variant="outlined"
      alwaysOn={alwaysOn}
      handleSearch={handleSearch}
      searchOnChange={true}
    />
  );
};

const getAccountFilters = (allAccounts, setAccounts) => [
  <AccountSearchField
    label="Account Search"
    allAccounts={allAccounts}
    setAccounts={setAccounts}
    alwaysOn
  />,
];

export const ServicesExportDialog = ({
  open,
  onClose,
  onSubmit,
  exportServices = [],
}) => {
  const classes = useStyles();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const highlightNames = useMemo(() => {
    return exportServices.map((service) => service?.name).filter(Boolean);
  }, [exportServices]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchActiveAccounts(notify);
        setAllAccounts(data);
        setAccounts(data);
      } finally {
        setLoading(false);
      }
    };
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const ids = useMemo(() => accounts.map((a) => a.id), [accounts]);
  const listContext = useList({
    data: accounts,
    ids,
    loaded: !loading,
    loading,
    basePath: "/accounts",
    resource: "accounts",
    perPage: 10,
    sort: { field: "displayName", order: "ASC" },
  });

  const handleConfirm = () => {
    const selectedIds = Array.isArray(listContext.selectedIds)
      ? listContext.selectedIds
      : [];
    if (!selectedIds.length) return;
    const selectedAccounts = accounts.filter((a) => selectedIds.includes(a.id));
    onSubmit && onSubmit(selectedAccounts);
  };

  const exportDisabled = !listContext.selectedIds?.length;

  return (
    <Dialog maxWidth="xl" open={open} fullWidth>
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <h2>Copy Services to Accounts</h2>
        <IconButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="large" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <ListContextProvider value={listContext}>
          <ListToolbar
            filters={getAccountFilters(allAccounts, setAccounts)}
            actions={<TopToolbar />}
          />
          <Datagrid
            rowClick="toggleSelection"
            hasBulkActions
            expand={<ServicesPeak highlightNames={highlightNames} />}
          >
            <TextField source="displayName" label="Display Name" />
            <TextField source="businessName" label="Account" />
            <TextField source="lotNumberPrefix" label="Lot Prefix" />
            <TextField source="addressState" label="State" />
          </Datagrid>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination {...listContext} />
          </div>
        </ListContextProvider>
      </DialogContent>
      <DialogActions>
        <div style={{ flex: 1 }}>
          <Typography variant="body2">
            Selected: {listContext.selectedIds?.length || 0}
          </Typography>
        </div>
        <Button label="Cancel" color="default" onClick={onClose}>
          <CancelIcon />
        </Button>
        {exportDisabled ? (
          <Tooltip title="No services selected" arrow placement="left">
            <span>
              <Button label="Export" disabled>
                <ExportIcon />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            label="Export"
            color="primary"
            onClick={handleConfirm}
            disabled={exportDisabled}
          >
            <ExportIcon />
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
